import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { Suspense, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import Navbar from "../components/Header/Navbar";
import Footer from "../components/Header/Footer";
import Loader from "../components/Reusable/Loader";
import ScrollToTop from "../components/Reusable/ScrollToTop";
import AuthModelContainer from "../components/Reusable/AuthModelContainer";
import GlobalAuthLoader from "../components/pagesSpecific/Login/GlobalAuthLoader";

import {
  openSignInModal,
  closeSignInModal,
  setResetCredentials,
  clearResetCredentials,
} from "../store/slices/authSlice";

import useSessionMonitor from "../hooks/useSessionMonitor";
import { AUTH_POPUP_VARIANTS } from "../constants/commonConstants";
import { useThunk } from "../hooks/useThunk";
import { getCurrentUserSubscriptionSummary } from "../store";

const Root = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  // const hasFetchedSummaryRef = useRef(false);
  const { isSignInModalOpen, resetCredentials, isAuthenticated } = useSelector(
    (state) => state.auth,
  );

  const [searchParams, setSearchParams] = useSearchParams();

  // Monitor token expiration
  useSessionMonitor();

  // const [doFetchUserSubscriptionSummary, isFetchingSummary] = useThunk(
  //   getCurrentUserSubscriptionSummary,
  // );

  /* ------------------------------------------
   * Get URL parameters only once
   * ------------------------------------------ */
  const authParams = useMemo(() => {
    const resetId = searchParams.get("id");
    const resetToken = searchParams.get("token");
    const showSignin = searchParams.get("signin") === "true";

    return {
      resetId,
      resetToken,
      hasResetParams: resetId && resetToken,
      showSignin,
    };
  }, [searchParams]);

  const initialVariant = useMemo(() => {
    if (authParams.hasResetParams) return AUTH_POPUP_VARIANTS.RESET_PASSWORD;
    if (authParams.showSignin) return AUTH_POPUP_VARIANTS.SIGNIN;
    return AUTH_POPUP_VARIANTS.SIGNIN;
  }, [authParams]);

  /* ------------------------------------------
   * Handle modal opening based on URL params
   * ------------------------------------------ */
  useEffect(() => {
    // Don't open modal if user is on /onboarding route and authenticated
    const isOnOnboarding =
      location.pathname === "/onboarding" ||
      location.pathname === "/signin" ||
      location.pathname === "/login";

    if (isSignInModalOpen) return;

    // Only open modal for URL params if not on onboarding pages
    if (!isOnOnboarding) {
      if (authParams.hasResetParams) {
        dispatch(
          setResetCredentials({
            id: authParams.resetId,
            token: authParams.resetToken,
          }),
        );
        dispatch(openSignInModal());
      } else if (authParams.showSignin && !isAuthenticated) {
        dispatch(openSignInModal());
      }
    }
  }, [
    authParams,
    dispatch,
    isSignInModalOpen,
    isAuthenticated,
    location.pathname,
  ]);

  // Fetch User Subscription Summary
  // useEffect(() => {
  //   if (!isAuthenticated) {
  //     hasFetchedSummaryRef.current = false;
  //     return;
  //   }

  //   if (hasFetchedSummaryRef.current) return;

  //   hasFetchedSummaryRef.current = true;
  //   doFetchUserSubscriptionSummary();
  // }, [isAuthenticated]);

  /* ------------------------------------------
   * Close modal handler
   * ------------------------------------------ */
  const handleModalClose = () => {
    dispatch(closeSignInModal());
    dispatch(clearResetCredentials());
    setSearchParams({});
  };

  return (
    <div className="flex flex-col min-h-screen min-w-[320px]">
      {/* Global loader for auth state */}
      <GlobalAuthLoader />

      {/* Navbar always fixed at top */}
      <div className="mt-16">
        <Navbar />
      </div>

      <main className="flex-1">
        {/* Auth modal */}
        <AuthModelContainer
          isOpen={isSignInModalOpen}
          onClose={handleModalClose}
          initialVariant={initialVariant}
          resetToken={resetCredentials?.token}
        />

        {/* Auto scroll to top on route change */}
        <ScrollToTop />

        {/* Handles lazy-loaded route children */}
        <Suspense
          fallback={<Loader text="Loading page..." showText={true} size={60} />}
        >
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10 py-4">
            <Outlet />
          </div>
        </Suspense>
      </main>

      <Footer />
    </div>
  );
};

export default Root;
